import { useState } from "react";
import { ExternalLink, MapPin } from "lucide-react";
import {
  ComposableMap, Geographies, Geography, Marker,
} from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Generic US pin map, ported from the old VEXOS Links module.
// `items` — [{ id, lat, lng, location, title, project_type, coverImageUrl }]
export default function USMap({ items, onPinClick }) {
  const [hovered, setHovered] = useState(null);
  const mappable = items.filter(i => i.lat != null && i.lng != null);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden" style={{ background: "#000000" }}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 880 }}
        viewBox="0 0 800 500"
        style={{ width: "100%", height: "auto", display: "block" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#000000"
                stroke="#121212"
                strokeWidth={0.6}
                style={{ outline: "none" }}
              />
            ))
          }
        </Geographies>

        {mappable.map(item => {
          const isHov = hovered?.id === item.id;
          return (
            <Marker
              key={item.id}
              coordinates={[item.lng, item.lat]}
              onClick={() => onPinClick(item)}
              onMouseEnter={() => setHovered(item)}
              onMouseLeave={() => setHovered(null)}
            >
              {isHov && <circle r={16} fill="rgba(255,255,255,0.07)" />}
              <circle r={isHov ? 10 : 8} fill="rgba(255,255,255,0.12)" />
              <circle r={isHov ? 5 : 4} fill="#ffffff" style={{ cursor: "pointer" }} />
            </Marker>
          );
        })}
      </ComposableMap>

      <div
        className="absolute top-4 right-4 text-xs font-medium px-3 py-1 rounded-full"
        style={{
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.35)",
          border: "1px solid rgba(255,255,255,0.08)",
          letterSpacing: "0.04em",
        }}
      >
        {mappable.length} / {items.length} pinned
      </div>

      {hovered && (
        <div
          className="absolute bottom-5 left-5 overflow-hidden rounded-xl w-52 pointer-events-none z-10"
          style={{ background: "#000000", border: "1px solid #121212", boxShadow: "0 20px 60px rgba(0,0,0,0.8)" }}
        >
          {hovered.coverImageUrl && (
            <img
              src={hovered.coverImageUrl}
              alt={hovered.location}
              className="w-full h-28 object-cover"
              style={{ filter: "brightness(0.92) contrast(1.05)" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="p-3.5">
            <p className="text-[13px] font-semibold text-white leading-snug">
              {hovered.title || hovered.location}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3 shrink-0" style={{ color: "rgba(255,255,255,0.3)" }} />
              <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                {hovered.location}
              </span>
            </div>
            {hovered.project_type && (
              <span
                className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full"
                style={{
                  background: "rgba(255,255,255,0.07)",
                  color: "rgba(255,255,255,0.4)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  letterSpacing: "0.03em",
                }}
              >
                {hovered.project_type}
              </span>
            )}
            <div className="flex items-center gap-1 mt-3 text-[11px]" style={{ color: "rgba(255,255,255,0.2)" }}>
              <ExternalLink className="w-3 h-3" />
              Click to open
            </div>
          </div>
        </div>
      )}

      {mappable.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-10 h-10 mx-auto mb-2" style={{ color: "rgba(255,255,255,0.15)" }} />
            <p className="text-sm font-medium" style={{ color: "rgba(255,255,255,0.3)" }}>No pins yet</p>
            <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.15)" }}>
              Projects are auto-pinned when saved.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
